import {
  Box,
  Flex,
  Heading,
  Icon,
  IconButton,
  SimpleGrid,
  Skeleton,
  Text,
  toast,
  Tooltip,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  DollarSign,
  Phone,
} from 'lucide-react';
import { useState } from 'react';
import DefaultPage from '../../components/shared/DefaultPage';
import { useParceirosAbaixo } from '../../services/hooks/useParceirosAbaixo';
import { phoneMask } from '../../utils/phoneMask';
import formatValue from '../../utils/formatValue';

const buildTree = data => {
  const tree = [];
  const lookup = {};

  data.forEach(item => {
    lookup[item.nivel] = { ...item, children: [] };
  });

  Object.values(lookup).forEach(item => {
    const parentLevel = item.nivel.split('.').slice(0, -1).join('.');
    if (parentLevel && lookup[parentLevel]) {
      lookup[parentLevel].children.push(item);
    } else {
      tree.push(item);
    }
  });

  return tree;
};

const RenderTree = ({ nodes, indent = 0 }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const toast = useToast();

  // estado que guarda os IDs abertos
  const [openNodes, setOpenNodes] = useState({});

  const toggleNode = id => {
    setOpenNodes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return nodes.map(node => {
    const hasChildren = node.children.length > 0;
    const isOpen = openNodes[node.id];

    return (
      <Flex key={node.id} pl={indent + 3} mt={2} flexDir="column" w="100%">
        <Flex align="center" w="100%">
          {hasChildren && (
            <IconButton
              aria-label={isOpen ? 'Fechar' : 'Abrir'}
              icon={
                isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />
              }
              onClick={() => toggleNode(node.id)}
              mr="2px"
              color={color}
              _focus={{ outline: 'none', boxShadow: 'none' }}
              bg="transparent"
              variant="ghost"
              size="sm"
            />
          )}
          {!hasChildren && <Box w="34px" />}
          <Flex
            align="center"
            bg={bg}
            px="3"
            rounded="2xl"
            borderWidth="1px"
            w="100%"
            justify="space-between"
          >
            <Flex>
              <Text fontWeight="bold" color={color} mr={2}>
                {node.nivel}
              </Text>
              <Text color={color} fontWeight="medium">
                {node.nome}
              </Text>
            </Flex>
            <Flex>
              <Flex borderRightWidth="1px" pl="3" align="center">
                <Text color={color}>{phoneMask(node.telefone)}</Text>
                <IconButton
                  aria-label="copiar"
                  margin="0px"
                  py="3"
                  bg="transparent"
                  variant="ghost"
                  rounded="0px"
                  icon={<Copy size="18px" color={color} />}
                  h="100%"
                  onClick={() => {
                    navigator.clipboard.writeText(node.telefone);
                    toast({
                      title: 'Copiado',
                      status: 'success',
                      isClosable: true,
                      duration: 500,
                      position: 'bottom-right',
                    });
                  }}
                />
              </Flex>
              <Flex
                borderWidth="0px"
                borderWidthBottom="1px"
                pl="3"
                align="center"
              >
                <Text color={color}>{node.email}</Text>
                <IconButton
                  aria-label="copiar"
                  margin="0px"
                  py="3"
                  bg="transparent"
                  variant="ghost"
                  rounded="0px"
                  icon={<Copy size="18px" color={color} />}
                  h="100%"
                  onClick={() => {
                    navigator.clipboard.writeText(node.email);
                    toast({
                      title: 'Copiado',
                      status: 'success',
                      isClosable: true,
                      duration: 500,
                      position: 'bottom-right',
                    });
                  }}
                />
              </Flex>
              <Flex ml="4">
                <Tooltip
                  placement="left-end"
                  label={
                    <Flex flexDir="column" p="2">
                      <Text color={bg}>
                        Custo certificado: {formatValue(node.custo_certificado)}
                      </Text>
                      <Text color={bg}>
                        Comissão sugerida:{' '}
                        {formatValue(node.comissao_certificado)}
                      </Text>
                    </Flex>
                  }
                >
                  <Flex align="center">
                    <Text color="green.500" fontWeight="medium">
                      {formatValue(node.valor_certificado)}
                    </Text>
                  </Flex>
                </Tooltip>
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        {hasChildren && isOpen && (
          <RenderTree nodes={node.children} indent={indent + 1} />
        )}
      </Flex>
    );
  });
};

const Hierarquia = () => {
  // estilos
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const bg = useColorModeValue('white', 'gray.800');

  const toast = useToast();

  // dados
  const { data, isLoading } = useParceirosAbaixo();
  const parceirosData = data ?? [];
  const treeData = buildTree(parceirosData);

  return (
    <DefaultPage>
      <Flex mt="12" mx="4" justify="space-between">
        <Box>
          <Heading fontSize="2xl">Hierarquia</Heading>
          <Heading fontSize="xl" mt="2" fontWeight="medium" color={color}>
            Visão organizada dos usuários do sistema.
          </Heading>
        </Box>
      </Flex>
      <Box m="4" ml="0" p="2">
        {isLoading && (
          <>
            <Skeleton w="100%" h="50px" rounded="2xl" />
          </>
        )}
        <RenderTree nodes={treeData} />
      </Box>
    </DefaultPage>
  );
};

export default Hierarquia;
